import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { setErrorMessage, setIsLoading, setIsToasterError } from "./app";
import API from "../../api/api";
import { setAdminLoader } from "./admin";

export interface ITonneCreateBody {
  email: string;
  userName: string;
  userSurname: string;
  place: string;
  street: string;
  homeNumber: string;
  hausNrZusatz?: string;
  message: string;
  phoneNumber: string;
  tonnenType: string;
  tonnenVolume: string;
  tonnenNummer?: string;
  tonnenDefectReason: string;
}

export interface ITonneDefektResponseBody extends ITonneCreateBody {
  id: string;
  createdAt: string;
  status?: string;
  history?: {
    id: string;
    createdAt: string;
    status: string;
    email: string;
    firstName?: string;
    lastName?: string;
  }[];
  objectId?: string;
}

export interface IReclamation {
  client: {
    places: {
      id: string;
      gemeindeBezeichnung: string;
      gemeindePostleitzahl: number;
      gemeindeNummer: number;
    }[];
    cityParts: {
      id: string;
      orteilBezeichnung: string;
      ortsteilNummer: number;
      ortsteilBezirk: string;
      gemeindeNummer: string;
    }[];
    streets: {
      id: string;
      gemeindeNummer: number;
      strasseBezeichnung: string;
      ortsteilNummer?: string;
    }[];
    errorMessage: string;
  };
  admin: {
    itemsCount: number;
    tonneDefektData: ITonneDefektResponseBody[];
    tonneDefektDataItem: ITonneDefektResponseBody | null;
    isTonneDefektDataLoading: boolean;
  };
}

const initialState: IReclamation = {
  client: {
    places: [],
    streets: [],
    cityParts: [],
    errorMessage: "",
  },
  admin: {
    itemsCount: 0,
    tonneDefektData: [],
    isTonneDefektDataLoading: false,
    tonneDefektDataItem: null,
  },
};

export const getViersenTonneDeffektStreetsAndPlaces = createAsyncThunk(
  "tonneDefekt/client/getViersenTonneDeffektStreetsAndPlaces",
  async (_, { dispatch }) => {
    try {
      dispatch(setIsLoading(true));
      const data =
        await API.tonneDefekt.get.getViersenTonneDeffektStreetsAndPlaces();

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setIsLoading(false));
    }
  },
);

export const createTonneDefekt = createAsyncThunk(
  "tonneDefekt/client/createTonneDefekt",
  async (
    data: ITonneCreateBody & { acceptHandler: () => void },
    { dispatch },
  ) => {
    const { acceptHandler, ...body } = data;
    try {
      dispatch(setIsLoading(true));
      const data = await API.tonneDefekt.post.createTonneDefekt(body);

      if (!data.error) {
        acceptHandler();
      }

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setIsLoading(false));
    }
  },
);

export const getAdminTonneDefekt = createAsyncThunk(
  "tonneDefekt/admin/getAdminTonneDefekt",
  async (
    {
      direction,
      page,
      pageSize,
      searchValue,
      sort,
      signal,
      endDate,
      startDate,
      status,
      // type,
    }: {
      sort?: string;
      direction?: string;
      searchValue?: string;
      page?: number;
      pageSize?: number;
      startDate?: Date | null | string;
      endDate?: Date | null | string;
      status?: string[];
      signal: AbortSignal;
      // type: string[];
    },
    { dispatch },
  ) => {
    try {
      dispatch(setAdminLoader(true));
      const data = API.tonneDefekt.get.getAdminTonneDeffekt({
        direction,
        page,
        pageSize,
        searchValue,
        sort,
        signal,
        endDate,
        startDate,
        status,
      });

      return data;
    } catch (error: any) {
      if (error !== "Request canceled") {
        dispatch(setIsToasterError(true));
        dispatch(setErrorMessage(error?.response?.data?.message));
      }
    } finally {
      dispatch(setAdminLoader(false));
    }
  },
);

export const changeTonneDefektItemsStatuses = createAsyncThunk(
  "tonneDefekt/admin/changeTonneDefektItemsStatuses",
  async ({ ids, status }: { ids: string[]; status: string }, { dispatch }) => {
    try {
      dispatch(setAdminLoader(true));

      const data = await API.tonneDefekt.put.changeItemsStatuses({
        ids,
        status,
      });

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setAdminLoader(false));
    }
  },
);

export const getAdminTonneDefektItemById = createAsyncThunk(
  "tonneDefekt/admin/getAdminTonneDefektItemById",
  async (id: string, { dispatch }) => {
    try {
      dispatch(setAdminLoader(true));
      const data = await API.tonneDefekt.get.getAdminTonneDeffektItemById(id);

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setAdminLoader(false));
    }
  },
);
export const updateStatusById = createAsyncThunk(
  "tonneDefekt/admin/updateStatusById",
  async (
    { id, status, message }: { id: string; status: string; message?: string },
    { dispatch },
  ) => {
    try {
      dispatch(setAdminLoader(true));
      const data = await API.tonneDefekt.put.updateStatusById({ id, status, message });

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setAdminLoader(false));
    }
  },
);

const tonneDefektSlice = createSlice({
  name: "tonneDefekt",
  initialState,
  reducers: {
    clearAdminTonneDefekt: (state) => {
      state.admin.itemsCount = 0;
      state.admin.tonneDefektData = [];
    },
    clearAdminTonneDefektItem: (state) => {
      state.admin.tonneDefektDataItem = null;
      state.admin.isTonneDefektDataLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getViersenTonneDeffektStreetsAndPlaces.pending,
      (state) => {},
    );
    builder.addCase(
      getViersenTonneDeffektStreetsAndPlaces.fulfilled,
      (state, action) => {
        if (action.payload) {
          state.client.places = action.payload.places;
          state.client.streets = action.payload.streets;
        }
      },
    );
    builder.addCase(
      getViersenTonneDeffektStreetsAndPlaces.rejected,
      (state) => {},
    );
    builder.addCase(getAdminTonneDefekt.pending, (state) => {});
    builder.addCase(getAdminTonneDefekt.fulfilled, (state, action) => {
      if (action.payload) {
        state.admin.itemsCount = action.payload.totalCount;
        state.admin.tonneDefektData = [...action.payload.data];
      }
    });
    builder.addCase(getAdminTonneDefekt.rejected, (state) => {});
    builder.addCase(changeTonneDefektItemsStatuses.pending, (state) => {});
    builder.addCase(
      changeTonneDefektItemsStatuses.fulfilled,
      (state, action) => {
        if (action.payload) {
          state.admin.tonneDefektData = state.admin.tonneDefektData.map(
            (el) => {
              const item = action.payload?.find((i) => i.id === el.id);
              if (item) {
                return item;
              }
              return el;
            },
          );
        }
      },
    );
    builder.addCase(changeTonneDefektItemsStatuses.rejected, (state) => {});
    builder.addCase(getAdminTonneDefektItemById.pending, (state) => {
      state.admin.isTonneDefektDataLoading = true;
    });
    builder.addCase(getAdminTonneDefektItemById.fulfilled, (state, action) => {
      if (action.payload) {
        state.admin.tonneDefektDataItem = action.payload;
      }
      state.admin.isTonneDefektDataLoading = false;
    });
    builder.addCase(getAdminTonneDefektItemById.rejected, (state) => {
      state.admin.isTonneDefektDataLoading = false;
    });
    builder.addCase(updateStatusById.pending, (state) => {
      state.admin.isTonneDefektDataLoading = true;
    });
    builder.addCase(updateStatusById.fulfilled, (state, action) => {
      if (action.payload) {
        state.admin.tonneDefektDataItem = action.payload;
      }
      state.admin.isTonneDefektDataLoading = false;
    });
    builder.addCase(updateStatusById.rejected, (state) => {
      state.admin.isTonneDefektDataLoading = false;
    });
    builder.addCase(createTonneDefekt.pending, (state) => {
      state.client.errorMessage = "";
    });
    builder.addCase(createTonneDefekt.fulfilled, (state, action) => {
      if (action.payload?.error) {
        state.client.errorMessage = action.payload?.errorMessage ?? "";
      }
    });
    builder.addCase(createTonneDefekt.rejected, (state) => {});
  },
});

export const { clearAdminTonneDefekt, clearAdminTonneDefektItem } =
  tonneDefektSlice.actions;

export const tonneDefektReducer = tonneDefektSlice.reducer;
